import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Link2, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { EmptyState, Field, Loader, PageHeader } from "@/components/common";
import { BLUE, CATEGORIES } from "@/lib/calendar";

const HOWTO = [
  ["Google Calendar", "Settings → your calendar → Integrate calendar → copy the \"Secret address in iCal format\"."],
  ["Outlook", "Settings → Calendar → Shared calendars → Publish calendar → copy the ICS link."],
  ["iCloud", "Calendar app → share the calendar → turn on Public Calendar → copy link."],
  ["School or team apps", "Look for \"Subscribe\" or an ICS link and copy it."],
];

function AddLink({ hubId, people, onSaved }) {
  const [f, setF] = useState({ name: "", url: "", category: "School", member_keys: [] });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/organizations/${hubId}/calendar/links`, f); toast.success(`${f.name || "Calendar"} linked`); setF({ name: "", url: "", category: "School", member_keys: [] }); onSaved(); } catch (x) {
      setErr(errMsg(x));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} noValidate className="q-card min-w-0 space-y-4" data-testid="link-add-form">
      <h2 className="font-display text-lg font-extrabold text-q-text">Add a calendar link</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
        <Field label="Name" htmlFor="ln-name"><input id="ln-name" className="q-input" placeholder="Lincoln Elementary" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="link-name-input" /></Field>
        <Field label="Category" htmlFor="ln-cat"><select id="ln-cat" className="q-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="link-category-select">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
      </div>
      <Field label="Calendar address (https:// or webcal://)" htmlFor="ln-url" error={err} errorTestid="link-url-error">
        <input id="ln-url" className="q-input" value={f.url} aria-invalid={!!err} onChange={(e) => { setF({ ...f, url: e.target.value }); setErr(""); }} placeholder="webcal://…" data-testid="link-url-input" />
      </Field>
      <div><p className="q-label">For</p><div className="flex flex-wrap gap-2">
        {people.map((p) => { const on = f.member_keys.includes(p.key); return <button type="button" key={p.key} aria-pressed={on} data-testid={`link-member-${p.name.split(" ")[0].toLowerCase()}`}
          onClick={() => setF((x) => ({ ...x, member_keys: on ? x.member_keys.filter((k) => k !== p.key) : [...x.member_keys, p.key] }))}
          className={`min-h-[44px] rounded-full border px-3 text-sm font-semibold ${on ? "border-transparent text-white" : "border-q-line text-q-muted"}`} style={on ? { background: `rgb(${BLUE})` } : undefined}>{p.name.split(" ")[0]}</button>; })}
      </div></div>
      <button className="q-btn q-btn-primary" disabled={busy || !f.url.trim()} data-testid="link-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add link</button>
    </form>
  );
}

function LinkRow({ l, hubId, canManage, onChanged }) {
  const [busy, setBusy] = useState(false);
  const tid = l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const act = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <li className="q-card flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center" data-testid={`link-row-${tid}`}>
      <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: `rgba(${BLUE},0.14)` }}><Link2 className="h-5 w-5" style={{ color: `rgb(${BLUE})` }} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-display font-extrabold text-q-text">{l.name} <span className="text-xs font-semibold text-q-faint">· {l.category}</span></p>
        <p className="break-all text-xs text-q-faint" data-testid={`link-url-${tid}`}>{l.url}</p>
        <p className="text-xs text-q-muted" data-testid={`link-count-${tid}`}>{l.count || 0} events{l.checked_at ? ` · checked ${new Date(l.checked_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}</p>
        {l.error && <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold" style={{ color: "rgb(var(--q-danger))" }} data-testid={`link-error-${tid}`}><AlertTriangle className="h-4 w-4" />{l.error}</p>}
      </div>
      {canManage && <div className="flex gap-2">
        <button className="q-btn q-btn-secondary min-h-[44px]" disabled={busy} onClick={() => act(() => api.post(`/organizations/${hubId}/calendar/links/${l.id}/refresh`))} data-testid={`link-refresh-${tid}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh now</button>
        <button className="q-btn q-btn-ghost h-11 w-11 p-0" disabled={busy} aria-label="Remove link" onClick={() => act(() => api.delete(`/organizations/${hubId}/calendar/links/${l.id}`), "Link removed")} data-testid={`link-remove-${tid}`}><Trash2 className="h-4 w-4" /></button>
      </div>}
    </li>
  );
}

export default function CalendarLinks() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [people, setPeople] = useState([]);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/calendar/links`).then(({ data }) => setD(data)), [hub.id]);
  useEffect(() => { load(); api.get(`/organizations/${hub.id}/calendar/people`).then(({ data }) => setPeople(data.people)); }, [load, hub.id]);
  if (!d) return <Loader />;
  return (
    <div data-testid="links-page">
      <PageHeader eyebrow="Calendar" title="Calendar Links" rgb={BLUE} testid="links-title" />
      <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          {d.links.length ? <ul className="space-y-3" data-testid="links-list">{d.links.map((l) => <LinkRow key={l.id} l={l} hubId={hub.id} canManage={d.can_manage} onChanged={load} />)}</ul>
            : <EmptyState Icon={Link2} rgb={BLUE} title="No linked calendars" hint="Subscribe to school, sports or work calendars. Their events show read-only." testid="links-empty" />}
          {d.can_manage ? <AddLink hubId={hub.id} people={people} onSaved={load} /> : <p className="text-sm text-q-muted" data-testid="links-parents-only">Only parents can add or remove calendar links.</p>}
        </div>
        <aside className="q-card h-fit" data-testid="links-howto">
          <h2 className="mb-3 font-display text-lg font-extrabold text-q-text">Where to find the link</h2>
          <ol className="space-y-3 text-sm">{HOWTO.map(([k, v]) => <li key={k} data-testid={`links-howto-${k.split(" ")[0].toLowerCase()}`}><p className="font-bold text-q-text">{k}</p><p className="text-q-muted">{v}</p></li>)}</ol>
          <p className="mt-4 text-xs text-q-faint">Linked events refresh about every 30 minutes. Change them in the original calendar.</p>
        </aside>
      </div>
    </div>
  );
}
